import { useState } from 'react';
import { UtensilsCrossed } from 'lucide-react';
import timesheetApprovalService from '../../services/timesheetApproval';
import { formatDateTimeInTz } from '../../utils/timezone';

interface Props {
  entryId: number;
  approvedByName: string | null | undefined;
  approvedAt: string | null | undefined;
  /** Only while the submission is still open for review. */
  canEdit: boolean;
  orgTimezone: string;
  onChange: (next: { approvedByName: string | null; approvedAt: string | null }) => void;
  /** Name of the person signed in, shown straight away after they record it. */
  myName: string;
}

/**
 * An entry where no lunch was taken. Working through the meal break attracts
 * penalty rates, and company policy makes it supervisor-directed only — so the
 * line says whether a supervisor asked for it, and lets one record that they did.
 */
export default function LunchWorkedApproval({ entryId, approvedByName, approvedAt, canEdit, orgTimezone, onChange, myName }: Props) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const approved = !!approvedAt;

  const set = async (next: boolean) => {
    setSaving(true);
    setError(null);
    try {
      const res = await timesheetApprovalService.setLunchApproval(entryId, next);
      if (!res.success) {
        setError(res.error.message || 'Could not save. Please try again.');
        return;
      }
      onChange({
        approvedAt: res.data.lunch_worked_approved_at ?? null,
        approvedByName: next ? myName : null,
      });
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`mt-1 rounded-md px-2 py-1.5 text-xs ${approved ? 'bg-sky-50 text-sky-800' : 'bg-orange-50 text-orange-800'}`}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <UtensilsCrossed className="h-3 w-3 shrink-0" aria-hidden="true" />
        <span className="min-w-0 break-words font-medium">
          {approved
            ? `Worked through lunch — asked by ${approvedByName || 'a supervisor'}${approvedAt ? `, ${formatDateTimeInTz(approvedAt, orgTimezone)}` : ''}`
            : 'Worked through lunch — not approved by a supervisor'}
        </span>
        {canEdit && (
          <button
            type="button"
            onClick={() => set(!approved)}
            disabled={saving}
            className="ml-auto shrink-0 rounded border border-current px-2 py-0.5 font-medium hover:bg-white/60 disabled:opacity-50"
          >
            {saving ? 'Saving…' : approved ? 'Withdraw' : 'I asked them to'}
          </button>
        )}
      </div>
      {!approved && (
        <p className="mt-0.5 text-orange-700">Penalty rates apply to a worked meal break. Record it here only if you asked them to work through.</p>
      )}
      {error && <p className="mt-0.5 text-red-700">{error}</p>}
    </div>
  );
}
